import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 34;

const ImportOrderList = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/import/import-order-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch import orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Vessel Info</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search by contract, vessel, commodity, supplier..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-60 text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No import orders found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Vessel Name</th>
                <th className="px-3 py-2 text-left font-medium">Contract No</th>
                <th className="px-3 py-2 text-left font-medium">Supplier</th>
                <th className="px-3 py-2 text-left font-medium">Commodity</th>
                <th className="px-3 py-2 text-left font-medium">BL Count</th>
                <th className="px-3 py-2 text-left font-medium">Order Type</th>
                <th className="px-3 py-2 text-left font-medium">Order Date</th>
                <th className="px-3 py-2 text-left font-medium">Expected Shipment Start</th>
                <th className="px-3 py-2 text-left font-medium">Expected Shipment End</th>
                <th className="px-3 py-2 text-left font-medium">Quantity (MT)</th>
                <th className="px-3 py-2 text-left font-medium">Unit Price (USD)</th>
                <th className="px-3 py-2 text-left font-medium">Incoterm</th>
                <th className="px-3 py-2 text-left font-medium">Total Cost (INR)</th>
                <th className="px-3 py-2 text-left font-medium">Unit Cost (INR)</th>
                <th className="px-3 py-2 text-left font-medium">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {records.map((order, idx) => (
                <tr key={`${order.vessel_name}-${order.purchase_contract_no}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1 font-semibold">{order.vessel_name}</td>
                  <td className="px-3 py-1 font-semibold">{order.purchase_contract_no}</td>
                  <td className="px-3 py-1">{order.supplier_name || '-'}</td>
                  <td className="px-3 py-1">{order.commodity_name || '-'}</td>
                  <td className="px-3 py-1">{order.bl_count}</td>
                  <td className="px-3 py-1">{order.order_type}</td>
                  <td className="px-3 py-1">
                    {order.order_date ? 
                      new Date(order.order_date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit'
                      }).replace(/\//g, '/') 
                    : ''}
                  </td>
                  <td className="px-3 py-1">
                    {order.expected_shipment_start ? 
                      new Date(order.expected_shipment_start).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit'
                      }).replace(/\//g, '/') 
                    : ''}
                  </td>
                  <td className="px-3 py-1">
                    {order.expected_shipment_end ? 
                      new Date(order.expected_shipment_end).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit'
                      }).replace(/\//g, '/') 
                    : ''}
                  </td>
                  <td className="px-3 py-1">
                    {typeof order.total_quantity_mt === 'number'
                      ? order.total_quantity_mt.toFixed(3)
                      : order.total_quantity_mt || ''}
                  </td>
                  <td className="px-3 py-1">
                    {typeof order.unit_price_usd === 'number'
                      ? order.unit_price_usd.toFixed(2)
                      : order.unit_price_usd || ''}
                  </td>
                  <td className="px-3 py-1">{order.incoterm}</td>
                  <td className="px-3 py-1">
                    {typeof order.total_landed_cost_inr === 'number'
                      ? order.total_landed_cost_inr.toLocaleString('en-IN')
                      : order.total_landed_cost_inr || ''}
                  </td>
                  <td className="px-3 py-1">
                    {typeof order.unit_cost_inr === 'number'
                      ? order.unit_cost_inr.toLocaleString('en-IN')
                      : order.unit_cost_inr || ''}
                  </td>
                  <td className="px-3 py-1">{order.combined_remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === 1 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === totalPages 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportOrderList;
